<template>
  <div class="flex flex-col gap-1.5">
    <span id="request-fields-label" class="text-sm font-medium">What do you need from students?</span>

    <div class="flex flex-col rounded-md border border-surface-200" role="group" aria-labelledby="request-fields-label"
      data-testid="request-field-picker">
      <div v-if="loading" class="flex flex-col gap-2 p-3" data-testid="request-fields-loading">
        <Skeleton v-for="n in 3" :key="n" height="1.75rem" />
      </div>

      <template v-else>
        <p class="px-3 pt-2.5 pb-1 text-xs font-semibold uppercase tracking-wide text-surface-500">Students can fill these in</p>
        <div v-for="field in groups.open" :key="field.key" class="flex items-center justify-between gap-3 px-3 py-1.5"
          :data-testid="`request-field-${field.key}`">
          <div class="flex min-w-0 items-center gap-2.5">
            <Checkbox :modelValue="isChosen(field.key)" binary :inputId="`pick-${field.key}`"
              @update:modelValue="(on: boolean) => toggle(field.key, on)" :data-testid="`pick-${field.key}`" />
            <label :for="`pick-${field.key}`" class="min-w-0 cursor-pointer text-sm">
              {{ field.label }}<span v-if="field.kind" class="ml-1.5 text-xs text-surface-500">{{ field.kind }}</span>
            </label>
          </div>
          <div v-if="chosen(field.key)" class="flex shrink-0 items-center gap-2">
            <Checkbox :modelValue="chosen(field.key)?.required" binary :inputId="`required-${field.key}`"
              @update:modelValue="(on: boolean) => setRequired(field.key, on)" :data-testid="`required-${field.key}`" />
            <label :for="`required-${field.key}`" class="cursor-pointer text-xs text-surface-600">Required</label>
          </div>
        </div>
        <p v-if="!groups.open.length" class="px-3 py-1.5 text-sm text-surface-500" data-testid="no-field-choices">
          Students cannot fill in any field yet.{{ canUpdate ? ' Open one below, or add a new detail.' : '' }}
        </p>

        <template v-if="groups.closed.length">
          <p class="mt-1.5 border-t border-surface-200 px-3 pt-2.5 pb-1 text-xs font-semibold uppercase tracking-wide text-surface-500">
            Students can't fill these in yet
          </p>
          <div v-for="row in groups.closed" :key="row.key" class="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-3 py-1.5"
            :data-testid="`closed-field-${row.key}`">
            <div class="flex min-w-0 flex-col">
              <span class="text-sm">{{ row.label }}</span>
              <small class="text-xs text-surface-500">{{ closedHint(row) }}</small>
            </div>
            <Button v-if="canUpdate && fieldsToOpen(row, rows).length" label="Let students fill this in" size="small" text
              :loading="savingKey === row.key" :disabled="savingKey !== null" @click="open(row)"
              :data-testid="`open-field-${row.key}`" />
          </div>
        </template>

        <form v-if="canCreate" class="mt-1.5 flex flex-col gap-1.5 border-t border-surface-200 px-3 py-2.5" @submit.prevent="add"
          data-testid="add-detail-form">
          <label for="new-detail" class="text-xs font-semibold uppercase tracking-wide text-surface-500">Add a new detail</label>
          <div class="flex gap-2">
            <InputText id="new-detail" v-model="newLabel" maxlength="120" placeholder="e.g. Previous school" class="min-w-0 flex-1"
              :invalid="!!addError" data-testid="new-detail-label" />
            <Button type="submit" label="Add" icon="pi pi-plus" size="small" outlined :loading="savingKey === 'new'"
              :disabled="!newLabel.trim() || savingKey !== null" data-testid="add-detail" />
          </div>
          <small v-if="addError" class="text-red-500" data-testid="add-detail-error">{{ addError }}</small>
          <small v-else class="text-surface-500">A short-text field students fill in. Change its type later under Fields.</small>
        </form>
      </template>
    </div>

    <small v-if="error" class="text-red-500" data-testid="error-fields">{{ error }}</small>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useUserStore } from '@/store/userStore';
import { AppPermissions, can } from '@/utils/permissions';
import { useFormErrors } from '@/composables/useFormErrors';
import { useStudentFields } from '@/composables/useStudentFields';
import { useStudentDataRequestFieldChoices } from '@/composables/queries/useStudentDataRequestFieldChoices';
import { chooseFields, type DataRequestDraft } from '@/utils/studentDataRequests';
import { dataRequestPickerGroups, fieldsToOpen, openedFieldsMessage, type StudentFieldRow } from '@/utils/studentFields';
import { STUDENT_ACCESS_OPTIONS } from '@/utils/studentProfileForm';
import { extractGraphQLError } from '@/utils/graphqlError';

/**
 * The data request wizard's first step: tick what students should fill in.
 * Staff who manage custom fields can open a field to students, or add a new
 * one, from here instead of leaving the request for Settings.
 */
const props = defineProps<{ error?: string; preselect?: ReadonlyArray<string> }>();
const fields = defineModel<DataRequestDraft['fields']>({ required: true });

const toast = useToast();
const userStore = useUserStore();
const permission = (action: string) => can(userStore.currentUser, action, AppPermissions.CustomFields.Resource);
const canView = computed(() => permission(AppPermissions.CustomFields.Actions.View));
const canUpdate = computed(() => permission(AppPermissions.CustomFields.Actions.Update));
const canCreate = computed(() => permission(AppPermissions.CustomFields.Actions.Create));

const { choices, loading: loadingChoices } = useStudentDataRequestFieldChoices();
const { rows, loading: loadingRows, savingKey, openToStudents, addDetail } = useStudentFields({ enabled: canView });
const groups = computed(() => dataRequestPickerGroups(choices.value, canView.value ? rows.value : []));
const loading = computed(() => (loadingChoices.value && !choices.value.length) || (canView.value && loadingRows.value && !rows.value.length));

const chosen = (key: string) => fields.value.find(f => f.key === key);
const isChosen = (key: string) => !!chosen(key);

function tick(keys: ReadonlyArray<string>) {
  const wanted = [...new Set([...fields.value.map(f => f.key), ...keys])];
  fields.value = chooseFields(fields.value, choices.value, wanted);
}

function toggle(key: string, on: boolean) {
  if (on) tick([key]);
  else fields.value = fields.value.filter(f => f.key !== key);
}

function setRequired(key: string, required: boolean) {
  fields.value = fields.value.map(f => (f.key === key ? { ...f, required } : f));
}

// Started from a field's "Ask students": tick it once the fresh list
// arrives. A cached copy can predate opening that field to students.
let preselected = false;
watch([choices, loadingChoices], ([list, loadingList]) => {
  if (preselected || !props.preselect?.length || loadingList || !list.length) return;
  preselected = true;
  tick(props.preselect);
}, { immediate: true });

const accessLabel = (row: StudentFieldRow) => STUDENT_ACCESS_OPTIONS.find(o => o.value === row.access)?.label ?? '';

/** Its type and what students can do now, or why opening it would not help. */
function closedHint(row: StudentFieldRow): string {
  if (!fieldsToOpen(row, rows.value).length) return row.note ?? 'Students cannot fill this in yet.';
  return [row.kind, accessLabel(row)].filter(Boolean).join(' · ');
}

async function open(row: StudentFieldRow) {
  try {
    // The last save refetches the list, so the field is there to tick.
    const opened = await openToStudents(row);
    tick([row.key, ...opened.map(r => r.key)]);
    toast.add({ severity: 'success', summary: 'Opened to students', detail: openedFieldsMessage([row, ...opened.filter(r => r.key !== row.key)]), life: 4000 });
  } catch (err) {
    console.error(err);
    toast.add({ severity: 'error', summary: `Could not open ${row.label}`, detail: extractGraphQLError(err, 'Please try again.'), life: 5000 });
  }
}

const newLabel = ref('');
const { handleServerErrors, clearErrors, getErrorMessage, getFirstErrorMessage } = useFormErrors();
const addFailure = ref('');
const addError = computed(() => getErrorMessage('label') || getErrorMessage('key') || getFirstErrorMessage() || addFailure.value);

async function add() {
  clearErrors();
  addFailure.value = '';
  try {
    const key = await addDetail(newLabel.value);
    if (key) tick([key]);
    newLabel.value = '';
  } catch (err) {
    handleServerErrors(err);
    if (!getFirstErrorMessage()) {
      console.error(err);
      addFailure.value = extractGraphQLError(err, 'Could not add the detail. Please try again.');
    }
  }
}
</script>
